import { PHYSICS, PLAYER, TILE } from '../config/config';
import { approach, sign } from '../core/math';
import type { World } from '../game/world';
import type { HeroPose } from '../gfx/art/hero';
import { C } from '../gfx/palette';
import type { Renderer } from '../gfx/renderer';
import { moveBody, resolveGrow } from '../world/physics';
import { Entity } from './entity';
import { EnergyOrb } from './orb';

export type Form = 'small' | 'big' | 'fire';

export type PlayerState =
  | 'normal'
  | 'transform'
  | 'dying'
  | 'pipe'
  | 'flagSlide'
  | 'flagWalk'
  | 'done';

const DUCK_H = 15;

/** Bloom, the player character: a compact state machine over the shared physics body. */
export class Player extends Entity {
  readonly kind = 'goal' as const; // never collides with itself; kind is unused for the player
  form: Form = 'small';
  state: PlayerState = 'normal';

  /** Timers. */
  transformTimer = 0;
  invuln = 0;
  prism = 0;
  orbCooldown = 0;
  coyote = 0;
  buffer = 0;
  deathTimer = 0;
  pipeTimer = 0;
  flagTimer = 0;

  jumping = false;
  ducking = false;
  skidding = false;
  /** Set while a growth is waiting for head room. */
  pendingForm: Form | null = null;
  /** Direction of the current pipe animation. */
  pipeDir: 'down' | 'right' | 'up' = 'down';
  flagDone = false;
  /** Flagpole slide target. */
  private poleX = 0;
  private groundY = 0;

  animDist = 0;
  private blinkT = 0;
  /** Feet position at the start of the current step (used for stomp tests). */
  prevBottom = 0;

  constructor(x: number, y: number) {
    super(x, y, PLAYER.smallW, PLAYER.smallH);
    this.facing = 1;
  }

  get isBig(): boolean {
    return this.form !== 'small';
  }

  get hasFire(): boolean {
    return this.form === 'fire';
  }

  get invincible(): boolean {
    return this.prism > 0;
  }

  get invulnerable(): boolean {
    return this.invuln > 0 || this.prism > 0 || this.state === 'transform';
  }

  /** No gameplay interaction while these states run. */
  get busy(): boolean {
    return this.state !== 'normal';
  }

  /* --------------------------------------------------------------- reset */

  resetTo(x: number, y: number, form: Form = 'small'): void {
    this.applyForm(form, true);
    this.x = x;
    this.y = y - this.h;
    this.vx = 0;
    this.vy = 0;
    this.state = 'normal';
    this.facing = 1;
    this.invuln = 0;
    this.prism = 0;
    this.transformTimer = 0;
    this.deathTimer = 0;
    this.pipeTimer = 0;
    this.flagTimer = 0;
    this.flagDone = false;
    this.jumping = false;
    this.ducking = false;
    this.skidding = false;
    this.pendingForm = null;
    this.onGround = false;
    this.animDist = 0;
    this.alive = true;
  }

  /* -------------------------------------------------------------- update */

  override update(dt: number, world: World): void {
    this.prevBottom = this.bottom;
    this.blinkT += dt;
    if (this.invuln > 0) this.invuln = Math.max(0, this.invuln - dt);
    if (this.prism > 0) this.prism = Math.max(0, this.prism - dt);
    if (this.orbCooldown > 0) this.orbCooldown -= dt;
    if (this.pendingForm) this.tryPendingForm(world);

    switch (this.state) {
      case 'normal':
        this.updateNormal(dt, world);
        break;
      case 'transform':
        this.transformTimer -= dt;
        if (this.transformTimer <= 0) this.state = 'normal';
        break;
      case 'dying':
        this.updateDying(dt);
        break;
      case 'pipe':
        this.updatePipe(dt);
        break;
      case 'flagSlide':
        this.updateFlagSlide(dt, world);
        break;
      case 'flagWalk':
        this.updateFlagWalk(dt, world);
        break;
      case 'done':
        break;
    }
  }

  private updateNormal(dt: number, world: World): void {
    const input = world.input;
    const dir = input.axisX();
    const running = input.down('run');

    // ---- ducking (big forms only) ----
    const wantDuck = this.isBig && input.down('down') && this.onGround;
    if (wantDuck && !this.ducking) this.setDuck(true);
    else if (!wantDuck && this.ducking && this.canStand(world)) this.setDuck(false);

    // ---- horizontal ----
    const maxSpeed = running ? PHYSICS.runMax : PHYSICS.walkMax;
    const moveDir = this.ducking && this.onGround ? 0 : dir;
    this.skidding = false;
    if (moveDir !== 0) {
      const reversing = sign(this.vx) !== 0 && sign(this.vx) !== moveDir;
      let accel: number;
      if (!this.onGround) accel = PHYSICS.airAccel;
      else if (reversing) accel = PHYSICS.skidDecel;
      else accel = running ? PHYSICS.runAccel : PHYSICS.groundAccel;
      this.vx = approach(this.vx, moveDir * maxSpeed, accel * dt);
      this.facing = moveDir;
      if (reversing && this.onGround && Math.abs(this.vx) > PHYSICS.skidThreshold) {
        this.skidding = true;
        if (world.rng.bool(0.5)) {
          world.particles.dust(this.centerX + moveDir * -4, this.bottom - 1, moveDir);
        }
      }
    } else {
      const decel = this.onGround ? PHYSICS.groundFriction : PHYSICS.airDrag;
      this.vx = approach(this.vx, 0, decel * dt);
    }
    // Letting go of run bleeds excess speed off instead of cutting it instantly.
    if (this.onGround && !running && Math.abs(this.vx) > PHYSICS.walkMax) {
      this.vx = approach(this.vx, sign(this.vx) * PHYSICS.walkMax, PHYSICS.groundFriction * dt);
    }

    // ---- jump ----
    if (this.onGround) {
      this.coyote = PHYSICS.coyoteTime;
      this.jumping = false;
    } else if (this.coyote > 0) {
      this.coyote -= dt;
    }
    if (input.jumpPressed()) this.buffer = PHYSICS.jumpBuffer;
    else if (this.buffer > 0) this.buffer -= dt;

    if (this.buffer > 0 && this.coyote > 0 && !this.jumping) {
      this.doJump(world);
    }

    // Variable jump height: release cuts the rise short.
    if (this.jumping && this.vy < 0 && !input.jumpHeld()) {
      this.vy *= PHYSICS.jumpCutFactor;
      this.jumping = false; // no further hold-gravity benefit
    }

    const g = this.jumping && this.vy < 0 && input.jumpHeld() ? PHYSICS.gravityJumpHold : PHYSICS.gravity;
    this.vy = Math.min(this.vy + g * dt, PHYSICS.maxFall);

    // ---- fire ----
    if (this.form === 'fire' && input.pressed('run') && this.orbCooldown <= 0) {
      this.shoot(world);
    }

    // ---- move & resolve ----
    const res = moveBody(this, world.map, dt, world.solidBoxes());
    if (res.riding) {
      // ride moving platforms
      this.x += res.riding.vx * dt;
    }
    if (this.onGround) {
      this.jumping = false;
      world.resetCombo();
    }
    if (this.hitCeiling) {
      const hit = res.hits.filter((h) => h.side === 'bottom');
      if (hit.length) {
        // The classic rule: the block under the player's centre is the one that reacts.
        let best = hit[0];
        let bestD = Infinity;
        for (const h of hit) {
          const d = Math.abs((h.tx + 0.5) * TILE - this.centerX);
          if (d < bestD) {
            bestD = d;
            best = h;
          }
        }
        world.hitBlock(best.tx, best.ty, 'player', this.isBig || this.invincible);
      }
    }

    this.animDist += Math.abs(this.vx) * dt;

    // ---- pipes ----
    if (input.down('down') && this.onGround) {
      const tx = Math.floor(this.centerX / TILE);
      const ty = Math.floor((this.bottom + 2) / TILE);
      const warp = world.warpAt(tx, ty, 'down');
      if (warp) world.beginWarp(warp);
    } else if (this.hitRight && input.down('right')) {
      const tx = Math.floor((this.x + this.w + 2) / TILE);
      const ty = Math.floor((this.y + this.h - 4) / TILE);
      const warp = world.warpAt(tx, ty, 'right');
      if (warp) world.beginWarp(warp);
    }

    // ---- pit ----
    if (this.y > world.map.pixelH + 32) this.kill(world, true);
  }

  private doJump(world: World): void {
    const boost = Math.abs(this.vx) * PHYSICS.jumpSpeedBonus;
    this.vy = -(PHYSICS.jumpVelocity + boost);
    this.jumping = true;
    this.onGround = false;
    this.coyote = 0;
    this.buffer = 0;
    world.sfx(this.isBig ? 'jumpBig' : 'jump');
    world.particles.puff(this.centerX, this.bottom - 1, C.mortar, 3);
  }

  private shoot(world: World): void {
    const live = world.entities.filter((e) => e.kind === 'orb' && e.alive).length;
    if (live >= PLAYER.maxOrbs) return;
    this.orbCooldown = PLAYER.orbCooldown;
    const ox = this.facing > 0 ? this.x + this.w : this.x - 8;
    const orb = new EnergyOrb(ox, this.y + (this.isBig ? 8 : 3), this.facing);
    world.add(orb);
    world.sfx('fire');
    world.particles.puff(orb.centerX, orb.centerY, C.emberLight, 3);
  }

  private updateDying(dt: number): void {
    this.deathTimer += dt;
    if (this.deathTimer > PLAYER.deathHang) {
      this.vy = Math.min(this.vy + PHYSICS.gravity * dt, PHYSICS.maxFall);
      this.y += this.vy * dt;
    }
  }

  private updatePipe(dt: number): void {
    this.pipeTimer -= dt;
    const speed = 26;
    if (this.pipeDir === 'down') this.y += speed * dt;
    else if (this.pipeDir === 'up') this.y -= speed * dt;
    else this.x += speed * dt;
  }

  private updateFlagSlide(dt: number, world: World): void {
    this.x = this.poleX;
    this.vx = 0;
    this.y += 90 * dt;
    if (this.bottom >= this.groundY) {
      this.y = this.groundY - this.h;
      this.state = 'flagWalk';
      this.flagTimer = 0;
      this.facing = 1;
      world.sfx('flag');
    }
  }

  private updateFlagWalk(dt: number, world: World): void {
    this.flagTimer += dt;
    if (this.flagTimer < 0.35) {
      this.vx = 0;
      return;
    }
    this.vx = 78;
    this.vy = Math.min(this.vy + PHYSICS.gravity * dt, PHYSICS.maxFall);
    moveBody(this, world.map, dt);
    this.animDist += Math.abs(this.vx) * dt;
    if (this.flagTimer > 2.9) {
      this.state = 'done';
      this.flagDone = true;
    }
  }

  /* ------------------------------------------------------------- actions */

  /** Bounce after stomping an enemy. */
  stompBounce(world: World): void {
    const held = world.input.jumpHeld();
    this.vy = -(held ? PHYSICS.stompBounceHeld : PHYSICS.stompBounce);
    this.jumping = held;
    this.onGround = false;
  }

  /** Take a hit: powered forms shrink, the small form dies. */
  hurt(world: World): void {
    if (this.invulnerable || this.busy) return;
    if (this.form === 'fire' || this.form === 'big') {
      this.setForm('small', world);
      this.invuln = PLAYER.hurtInvuln;
      this.state = 'transform';
      this.transformTimer = PLAYER.transformTime;
      world.sfx('shrink');
      world.freeze(PLAYER.transformTime * 0.5);
      world.particles.puff(this.centerX, this.centerY, C.white, 8);
    } else {
      this.kill(world, false);
    }
  }

  kill(world: World, pit: boolean): void {
    if (this.state === 'dying' || this.state === 'done') return;
    this.state = 'dying';
    this.deathTimer = pit ? PLAYER.deathHang : 0;
    this.vx = 0;
    this.vy = pit ? PHYSICS.maxFall : -PLAYER.deathPop;
    this.ducking = false;
    world.onPlayerDeath();
  }

  grow(world: World): void {
    if (this.form === 'small') {
      this.setForm('big', world);
      world.sfx('grow');
      world.freeze(PLAYER.transformTime);
      this.state = 'transform';
      this.transformTimer = PLAYER.transformTime;
    }
    world.particles.puff(this.centerX, this.centerY, C.berryLight, 8);
  }

  giveFire(world: World): void {
    this.setForm('fire', world);
    world.sfx('grow');
    world.freeze(PLAYER.transformTime * 0.7);
    this.state = 'transform';
    this.transformTimer = PLAYER.transformTime * 0.7;
    world.particles.puff(this.centerX, this.centerY, C.emberLight, 10);
  }

  givePrism(world: World): void {
    this.prism = PLAYER.prismTime;
    world.particles.puff(this.centerX, this.centerY, C.prismLight, 12);
  }

  startPipe(dir: 'down' | 'right' | 'up', duration: number): void {
    this.state = 'pipe';
    this.pipeDir = dir;
    this.pipeTimer = duration;
    this.vx = 0;
    this.vy = 0;
    this.ducking = false;
  }

  endPipe(): void {
    this.state = 'normal';
    this.vx = 0;
    this.vy = 0;
    this.jumping = false;
  }

  startFlagSlide(poleX: number, groundY: number): void {
    this.state = 'flagSlide';
    this.poleX = poleX;
    this.groundY = groundY;
    this.vx = 0;
    this.vy = 0;
    this.facing = 1;
    this.ducking = false;
  }

  /* --------------------------------------------------------------- forms */

  private setForm(form: Form, world: World): void {
    if (form === this.form) return;
    const applied = this.applyForm(form, false, world);
    if (!applied) this.pendingForm = form;
  }

  /**
   * Resize the hitbox around the feet. When growing without head room the change is
   * deferred (pendingForm) instead of pushing the player inside a block.
   */
  private applyForm(form: Form, force: boolean, world?: World): boolean {
    const big = form !== 'small';
    const w = big ? PLAYER.bigW : PLAYER.smallW;
    const h = big ? PLAYER.bigH : PLAYER.smallH;
    const bottom = this.y + this.h;
    const cx = this.x + this.w / 2;
    const nx = Math.round(cx - w / 2);
    if (force || !world) {
      this.form = form;
      this.w = w;
      this.h = h;
      this.x = nx;
      this.y = bottom - h;
      this.ducking = false;
      this.pendingForm = null;
      return true;
    }
    const ny = resolveGrow(world.map, nx, bottom, w, h, TILE);
    if (ny === null) return false;
    this.form = form;
    this.w = w;
    this.h = h;
    this.x = nx;
    this.y = ny;
    this.ducking = false;
    this.pendingForm = null;
    return true;
  }

  private tryPendingForm(world: World): void {
    const form = this.pendingForm;
    if (!form) return;
    if (this.applyForm(form, false, world)) this.pendingForm = null;
  }

  private setDuck(on: boolean): void {
    if (on === this.ducking) return;
    const bottom = this.bottom;
    this.ducking = on;
    this.h = on ? DUCK_H : PLAYER.bigH;
    this.y = bottom - this.h;
  }

  private canStand(world: World): boolean {
    return resolveGrow(world.map, this.x, this.bottom, this.w, PLAYER.bigH, 0) !== null;
  }

  /* ---------------------------------------------------------------- draw */

  get pose(): HeroPose {
    switch (this.state) {
      case 'dying':
        return 'dead';
      case 'flagSlide':
        return 'flag';
      case 'pipe':
        return 'idle';
      default:
        break;
    }
    if (this.state === 'transform' && this.invuln > 0) return 'hurt';
    if (this.ducking) return 'duck';
    if (!this.onGround && this.state !== 'flagWalk') return this.vy < 0 ? 'jump' : 'fall';
    if (this.skidding) return 'skid';
    if (Math.abs(this.vx) > 3) {
      const frame = Math.floor(this.animDist / 9) % 3;
      const fast = Math.abs(this.vx) > PHYSICS.walkMax + 6;
      return (fast ? `run${frame}` : `walk${frame}`) as HeroPose;
    }
    return 'idle';
  }

  private spriteName(): string {
    const form = this.form === 'fire' ? 'fire' : this.form === 'big' ? 'big' : 'small';
    let pose = this.pose;
    if (form === 'small' && pose === 'duck') pose = 'idle';
    return `hero.${form}.${pose}`;
  }

  override draw(r: Renderer): void {
    // blink during damage invulnerability
    if (this.invuln > 0 && this.state !== 'dying' && Math.floor(this.blinkT * 24) % 2 === 0) return;

    const name = this.spriteName();
    const faceRight = this.facing >= 0;
    const buf = this.prism > 0 ? null : r.sprites.facing(name, faceRight);
    // Art overhangs the hitbox, so centre it horizontally and align the feet.
    const art = buf ?? r.sprites.get(name);
    const cx = this.x + this.w / 2 - art.w / 2;
    const cy = this.bottom - art.h;

    if (buf) {
      r.blit(buf, cx, cy);
    } else {
      const cycle = [C.white, C.prismLight, C.gold, C.prism];
      const color = cycle[Math.floor(this.prism * 18) % cycle.length];
      r.blit(r.sprites.tint(name, color, faceRight), cx, cy);
    }
  }

  /* ---------------------------------------------------------- diagnostics */

  /** Convenience for the debug overlay. */
  get debugLine(): string {
    return `${this.form} ${this.state} vx${this.vx.toFixed(0)} vy${this.vy.toFixed(0)}${
      this.onGround ? ' G' : ''
    }${this.prism > 0 ? ' PRISM' : ''}${this.invuln > 0 ? ' INV' : ''}`;
  }
}
